/**
 * The two languages the product ships in (contracts.md "Локализация
 * клиента"): every user-facing sentence is a method, so a missing
 * translation is a type error, not a fallback. The desktop's i18n.rs is the
 * pattern; this is its web twin with the strings F1–F3 need.
 *
 * Service-message wording lives here and not on the wire (D71): the server
 * sends the fact, the client writes the sentence in the reader's language.
 */

export type Lang = "ru" | "en";

export function detectLang(): Lang {
  const prefs = navigator.languages ?? [navigator.language];
  for (const p of prefs) {
    if (p.toLowerCase().startsWith("ru")) return "ru";
    if (p.toLowerCase().startsWith("en")) return "en";
  }
  return "en";
}

/** Russian needs three plural forms; English two. */
function plural(lang: Lang, n: number, one: string, few: string, many: string): string {
  if (lang === "en") return n === 1 ? one : many;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export interface Dict {
  // Login / register
  loginTitle(): string;
  loginUsername(): string;
  loginPassword(): string;
  loginEmail(): string;
  loginSubmit(): string;
  registerSubmit(): string;
  loginSwitchToRegister(): string;
  loginSwitchToLogin(): string;
  loginAgeGate(): string;
  loginFailed(reason: string): string;

  // Profile
  profileChangePhoto(): string;
  profileDisplayName(): string;
  profileBio(): string;
  profileChangePassword(): string;

  // Shell / sidebar
  channels(): string;
  allTab(): string;
  friendsTab(): string;
  searchPlaceholder(): string;
  newChannel(): string;
  members(n: number): string;
  connecting(): string;
  reconnecting(): string;
  offline(): string;

  // Feed
  today(): string;
  yesterday(): string;
  unreadSeparator(): string;
  edited(): string;
  unsupportedMessage(): string;
  unsupportedFormatting(): string;
  unsupportedAttachment(): string;
  loadOlder(): string;
  emptyChannel(): string;

  // Composer
  composerPlaceholder(): string;
  composerJoinFirst(): string;
  joinChannel(): string;
  joinPassword(): string;
  joinWrongPassword(): string;
  slowModeWait(seconds: number): string;
  messageTooLongSplit(parts: number): string;
  send(): string;
  recordingCancelled(): string;
  /** «Send as picture / send as file» on a staged picture (D233). */
  sendAs(): string;
  sendAsPicture(): string;
  sendAsPictureHint(): string;
  sendAsFile(): string;
  sendAsFileHint(maxBytes: number): string;
  originalOverLimit(maxBytes: number): string;
  typing(names: string[]): string;

  // The user card (F3)
  cardAdd(): string;
  cardPending(): string;
  cardAccept(): string;
  cardDecline(): string;
  cardMessage(): string;
  cardUnfriend(): string;
  cardBlock(): string;
  cardUnblock(): string;
  cardPolicyClosed(): string;
  requestsTitle(): string;
  toastRequest(who: string): string;
  toastAccepted(who: string): string;

  // Calls (read-only, F3)
  ringIncoming(who: string, channel: string): string;
  ringJoinHint(): string;
  ringDecline(): string;
  callPill(n: number): string;

  // Voice (F4)
  voiceJoin(): string;
  voiceConnecting(): string;
  voiceKeyExchange(): string;
  voiceLiveRtt(ms: number): string;
  voiceMutedState(): string;
  voiceDeafenedState(): string;
  voicePoorLoss(pct: number): string;
  voicePoorRtt(ms: number): string;
  voiceMute(): string;
  voiceUnmute(): string;
  voiceDeafen(): string;
  voiceUndeafen(): string;
  voiceDisconnect(): string;
  /** R16, said where the code is shown: voice is E2EE, the text chat is not. */
  voiceE2eeNote(): string;
  voiceCode(): string;
  voiceMicDenied(): string;
  voiceUnsupported(): string;
  voiceDropped(): string;

  // Streams (F5)
  watchStream(): string;
  stopWatching(): string;
  streamOf(name: string): string;
  streamPaused(): string;
  shareVolume(): string;
  streamEndedStopped(): string;
  streamEndedSourceGone(): string;
  streamEndedLeft(): string;
  streamEndedAdmin(): string;

  // Channel console (F6)
  consoleTitle(name: string): string;
  consoleDone(): string;
  consoleCancel(): string;
  consoleAccess(): string;
  consolePrivate(): string;
  consolePassword(): string;
  consolePwKept(): string;
  consolePwNone(): string;
  consolePwRemove(): string;
  consolePwWillRemove(): string;
  consoleLimits(): string;
  consoleVoiceLimit(s: number): string;
  consoleMediaLimit(mb: number): string;
  consoleCharsLimit(n: number): string;
  consoleSlowmode(s: number): string;
  consoleVoiceEnabled(): string;
  consoleInvite(): string;
  consoleInviteNone(): string;
  consoleInviteCreate(): string;
  consoleInviteCopy(): string;
  consoleInviteCopied(): string;
  consoleInviteRefresh(): string;
  consoleInviteRevoke(): string;
  consoleInviteTtl(): string;
  consoleAppearance(): string;
  consoleBgLight(): string;
  consoleBgDark(): string;
  consoleBgPick(): string;
  consoleBgRemove(): string;
  consoleBgSet(): string;
  consoleBgNone(): string;
  consoleBgWillRemove(): string;
  consoleBgBlur(n: number): string;
  consoleBgBrightness(n: number): string;
  consolePeople(): string;
  consoleRoleOwner(): string;
  consoleRoleAdmin(): string;
  consoleMakeAdmin(): string;
  consoleRevokeAdmin(): string;
  consoleMakeOwner(): string;
  consoleVoiceMute(): string;
  consoleVoiceUnmute(): string;
  consoleKick(): string;
  consoleBanDay(): string;
  consoleBanForever(): string;
  consoleBanned(): string;
  consoleBanUntil(when: string): string;
  consoleBanPermanent(): string;
  consoleUnban(): string;

  // Reports (F6)
  menuReport(): string;
  reportTitle(): string;
  reportReasonLabel(key: string): string;
  reportCommentPlaceholder(): string;
  reportSend(): string;
  reportSent(): string;
  reportsTitle(): string;
  reportsOpen(n: number): string;
  reportsShowResolved(): string;
  reportsRemove(): string;
  reportsDismiss(): string;
  reportsEmpty(): string;


  // Message menu
  menuReact(): string;
  menuReply(): string;
  menuEdit(): string;
  menuDelete(): string;

  // Service messages (D71) — sender first, then the object where one exists.
  svcCallStarted(who: string): string;
  svcCallLive(participants: number): string;
  svcCallEnded(duration: string | null): string;
  svcJoined(who: string): string;
  svcLeft(who: string): string;
  svcRemoved(who: string, whom: string): string;
  svcAdminGranted(who: string, whom: string): string;
  svcAdminRevoked(who: string, whom: string): string;
  svcRenamed(who: string, name: string): string;
  svcBanned(who: string, whom: string, until: string | null): string;
  svcUnbanned(who: string, whom: string): string;
  svcVoiceMuted(who: string, whom: string, until: string | null): string;
  svcVoiceUnmuted(who: string, whom: string): string;
  svcOwnerChanged(who: string, whom: string): string;
  svcUnknown(): string;
  /** Short, name-free sidebar/reply preview per service kind. */
  svcPreview(kind: string): string;

  // Durations
  durationS(totalSeconds: number): string;
}

const ru: Dict = {
  loginTitle: () => "EchoFloat",
  loginUsername: () => "Имя пользователя",
  loginPassword: () => "Пароль",
  loginEmail: () => "Почта",
  loginSubmit: () => "Войти",
  registerSubmit: () => "Создать аккаунт",
  loginSwitchToRegister: () => "Нет аккаунта? Регистрация",
  loginSwitchToLogin: () => "Уже есть аккаунт? Войти",
  loginAgeGate: () => "Мне есть 13 лет, и я принимаю правила",
  loginFailed: (reason) => reason,
  profileChangePhoto: () => "Изменить фото",
  profileDisplayName: () => "Имя в приложении",
  profileBio: () => "О себе",
  profileChangePassword: () => "Сменить пароль",

  channels: () => "Каналы",
  allTab: () => "Все",
  friendsTab: () => "Друзья",
  searchPlaceholder: () => "Поиск",
  newChannel: () => "Новый канал",
  members: (n) => `${n} ${plural("ru", n, "участник", "участника", "участников")}`,
  connecting: () => "Подключение…",
  reconnecting: () => "Переподключение…",
  offline: () => "Нет соединения",

  today: () => "Сегодня",
  yesterday: () => "Вчера",
  unreadSeparator: () => "Непрочитанные",
  edited: () => "изменено",
  unsupportedMessage: () => "Обновите EchoFloat, чтобы открыть сообщение",
  unsupportedFormatting: () => "Часть оформления не показана",
  unsupportedAttachment: () => "Обновите EchoFloat, чтобы открыть вложение",
  loadOlder: () => "Ранее",
  emptyChannel: () => "Начните разговор",

  composerPlaceholder: () => "Сообщение",
  composerJoinFirst: () => "Вступите, чтобы писать",
  joinChannel: () => "Вступить",
  joinPassword: () => "Пароль канала",
  joinWrongPassword: () => "Неверный пароль",
  slowModeWait: (s) => `Подождите ${s} с`,
  messageTooLongSplit: (n) =>
    `Сообщение уйдёт ${n} ${plural("ru", n, "частью", "частями", "частями")}`,
  send: () => "Отправить",
  recordingCancelled: () => "Запись отменена",
  sendAs: () => "Отправить как",
  sendAsPicture: () => "Картинку",
  sendAsPictureHint: () => "1024 px, до 300 КиБ",
  sendAsFile: () => "Файл",
  sendAsFileHint: (b) => `Оригинал, до ${Math.floor(b / (1024 * 1024))} МиБ`,
  originalOverLimit: (b) =>
    `Оригиналом можно отправить файл до ${
      Math.floor(b / (1024 * 1024))
    } МиБ — этот больше, отправьте его картинкой`,
  typing: (names) =>
    names.length === 1
      ? `${names[0]} печатает…`
      : `${names.join(", ")} печатают…`,

  cardAdd: () => "В друзья",
  cardPending: () => "Заявка отправлена",
  cardAccept: () => "Принять",
  cardDecline: () => "Отклонить",
  cardMessage: () => "Написать",
  cardUnfriend: () => "Удалить из друзей",
  cardBlock: () => "Заблокировать",
  cardUnblock: () => "Разблокировать",
  cardPolicyClosed: () => "Заявки закрыты",
  requestsTitle: () => "Заявки в друзья",
  toastRequest: (who) => `${who}: заявка в друзья`,
  toastAccepted: (who) => `${who}: заявка принята`,

  ringIncoming: (who, channel) => `${who} звонит в «${channel}»`,
  ringJoinHint: () => "Ответить",
  ringDecline: () => "Отклонить",
  callPill: (n) => `Звонок · ${n}`,

  voiceJoin: () => "Войти",
  voiceConnecting: () => "Соединяемся…",
  voiceKeyExchange: () => "Шифруем…",
  voiceLiveRtt: (ms) => `Микрофон включён · ${ms} мс`,
  voiceMutedState: () => "Микрофон выключен",
  voiceDeafenedState: () => "Звук отключён",
  voicePoorLoss: (pct) => `Плохое соединение · потери ${pct.toFixed(0)}%`,
  voicePoorRtt: (ms) => `Плохое соединение · ${ms} мс`,
  voiceMute: () => "Выключить микрофон",
  voiceUnmute: () => "Включить микрофон",
  voiceDeafen: () => "Выключить звук",
  voiceUndeafen: () => "Включить звук",
  voiceDisconnect: () => "Выйти",
  voiceE2eeNote: () => "Звонок защищён сквозным шифрованием. Чат — нет",
  voiceCode: () => "Код проверки",
  voiceMicDenied: () => "Разрешите доступ к микрофону",
  voiceUnsupported: () => "Обновите Chrome, чтобы звонить",
  voiceDropped: () => "Звонок оборвался",

  watchStream: () => "Смотреть стрим",
  stopWatching: () => "Закрыть стрим",
  streamOf: (name) => `Стрим ${name}`,
  streamPaused: () => "Стрим на паузе",
  shareVolume: () => "Громкость стрима",
  streamEndedStopped: () => "Стрим завершён",
  streamEndedSourceGone: () => "Окно закрыто",
  streamEndedLeft: () => "Автор вышел из звонка",
  streamEndedAdmin: () => "Администратор остановил стрим",

  consoleTitle: (name) => `«${name}»: настройки`,
  consoleDone: () => "Готово",
  consoleCancel: () => "Отмена",
  consoleAccess: () => "Доступ",
  consolePrivate: () => "Закрытый канал",
  consolePassword: () => "Пароль",
  consolePwKept: () => "без изменений",
  consolePwNone: () => "нет пароля",
  consolePwRemove: () => "Убрать",
  consolePwWillRemove: () => "Пароль удалится после сохранения",
  consoleLimits: () => "Лимиты",
  consoleVoiceLimit: (s) => `Голосовое до ${s} с`,
  consoleMediaLimit: (mb) => `Файлы: до ${mb} МБ`,
  consoleCharsLimit: (n) => `Сообщение до ${n} символов`,
  consoleSlowmode: (s) => (s === 0 ? "Медленный режим выключен" : `Пауза между сообщениями: ${s} с`),
  consoleVoiceEnabled: () => "Звонки",
  consoleInvite: () => "Приглашение",
  consoleInviteNone: () => "Ссылки нет",
  consoleInviteCreate: () => "Создать",
  consoleInviteCopy: () => "Копировать",
  consoleInviteCopied: () => "Ссылка скопирована",
  consoleInviteRefresh: () => "Новая ссылка",
  consoleInviteRevoke: () => "Удалить",
  consoleInviteTtl: () => "Ссылка действует 3 дня. Новая заменит старую",
  consoleAppearance: () => "Оформление",
  consoleBgLight: () => "Фон в светлой теме",
  consoleBgDark: () => "Фон в тёмной теме",
  consoleBgPick: () => "Выбрать",
  consoleBgRemove: () => "Убрать",
  consoleBgSet: () => "установлен",
  consoleBgNone: () => "нет",
  consoleBgWillRemove: () => "будет убран",
  consoleBgBlur: (n) => `Размытие фона: ${n}`,
  consoleBgBrightness: (n) => `Яркость фона: ${n}`,
  consolePeople: () => "Участники",
  consoleRoleOwner: () => "владелец",
  consoleRoleAdmin: () => "админ",
  consoleMakeAdmin: () => "Сделать админом",
  consoleRevokeAdmin: () => "Убрать из админов",
  consoleMakeOwner: () => "Передать канал",
  consoleVoiceMute: () => "Выключить микрофон",
  consoleVoiceUnmute: () => "Включить микрофон",
  consoleKick: () => "Исключить",
  consoleBanDay: () => "Заблокировать на день",
  consoleBanForever: () => "Заблокировать навсегда",
  consoleBanned: () => "Заблокированные",
  consoleBanUntil: (when) => `до ${when}`,
  consoleBanPermanent: () => "навсегда",
  consoleUnban: () => "Разблокировать",

  menuReport: () => "Пожаловаться",
  reportTitle: () => "Жалоба",
  reportReasonLabel: (key) => {
    switch (key) {
      case "abuse": return "Оскорбления";
      case "sexual": return "Откровенный контент";
      case "child": return "Опасность для детей";
      case "violence": return "Насилие";
      case "illegal": return "Незаконное";
      case "spam": return "Спам";
      default: return "Другое";
    }
  },
  reportCommentPlaceholder: () => "Комментарий (необязательно)",
  reportSend: () => "Отправить",
  reportSent: () => "Жалоба отправлена",
  reportsTitle: () => "Жалобы",
  reportsOpen: (n) => `Открытых: ${n}`,
  reportsShowResolved: () => "Показать закрытые",
  reportsRemove: () => "Удалено",
  reportsDismiss: () => "Отклонить",
  reportsEmpty: () => "Жалоб нет",


  menuReact: () => "Реакция",
  menuReply: () => "Ответить",
  menuEdit: () => "Изменить",
  menuDelete: () => "Удалить",

  svcCallStarted: (who) => `${who} начинает звонок`,
  svcCallLive: (n) => `Идёт звонок · ${n} ${plural("ru", n, "участник", "участника", "участников")}`,
  svcCallEnded: (d) => (d ? `Звонок завершён · ${d}` : "Звонок завершён"),
  svcJoined: (who) => `${who} теперь в канале`,
  svcLeft: (who) => `${who} покидает канал`,
  svcRemoved: (who, whom) => `${who} исключает ${whom}`,
  svcAdminGranted: (who, whom) => `${who} назначает ${whom} администратором`,
  svcAdminRevoked: (who, whom) => `${who} снимает с ${whom} права администратора`,
  svcRenamed: (who, name) => `${who} переименовывает канал в «${name}»`,
  svcBanned: (who, whom, until) =>
    until ? `${who} блокирует ${whom} до ${until}` : `${who} блокирует ${whom}`,
  svcUnbanned: (who, whom) => `${who} снимает блокировку с ${whom}`,
  svcVoiceMuted: (who, whom, until) =>
    until
      ? `${who} выключает микрофон ${whom} до ${until}`
      : `${who} выключает микрофон ${whom}`,
  svcVoiceUnmuted: (who, whom) => `${who} возвращает ${whom} микрофон`,
  svcOwnerChanged: (who, whom) => `${who} передаёт канал ${whom}`,
  svcUnknown: () => "Обновите EchoFloat, чтобы увидеть событие",
  svcPreview: (kind) => {
    switch (kind) {
      case "call": return "Звонок";
      case "joined": return "Новый участник";
      case "left": return "Участник вышел";
      case "removed": return "Участник исключён";
      case "admin_granted": return "Новый администратор";
      case "admin_revoked": return "Администратор снят";
      case "renamed": return "Канал переименован";
      case "banned": return "Блокировка";
      case "unbanned": return "Блокировка снята";
      case "voice_muted": return "Микрофон выключен";
      case "voice_unmuted": return "Микрофон включён";
      case "owner_changed": return "Новый владелец";
      default: return "Событие";
    }
  },

  durationS: (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s} с`;
    return s === 0 ? `${m} мин` : `${m} мин ${s} с`;
  },
};

const en: Dict = {
  loginTitle: () => "EchoFloat",
  loginUsername: () => "Username",
  loginPassword: () => "Password",
  loginEmail: () => "Email",
  loginSubmit: () => "Sign in",
  registerSubmit: () => "Create account",
  loginSwitchToRegister: () => "No account? Register",
  loginSwitchToLogin: () => "Have an account? Sign in",
  loginAgeGate: () => "I am 13 or older and accept the terms",
  loginFailed: (reason) => reason,
  profileChangePhoto: () => "Change photo",
  profileDisplayName: () => "Name in the app",
  profileBio: () => "Bio",
  profileChangePassword: () => "Change password",

  channels: () => "Channels",
  allTab: () => "All",
  friendsTab: () => "Friends",
  searchPlaceholder: () => "Search",
  newChannel: () => "New channel",
  members: (n) => `${n} ${plural("en", n, "member", "members", "members")}`,
  connecting: () => "Connecting…",
  reconnecting: () => "Reconnecting…",
  offline: () => "No connection",

  today: () => "Today",
  yesterday: () => "Yesterday",
  unreadSeparator: () => "Unread",
  edited: () => "edited",
  unsupportedMessage: () => "Update EchoFloat to open this message",
  unsupportedFormatting: () => "Some formatting isn't shown",
  unsupportedAttachment: () => "Update EchoFloat to open this attachment",
  loadOlder: () => "Earlier",
  emptyChannel: () => "Start the conversation",

  composerPlaceholder: () => "Message",
  composerJoinFirst: () => "Join to write",
  joinChannel: () => "Join",
  joinPassword: () => "Channel password",
  joinWrongPassword: () => "Wrong password",
  slowModeWait: (s) => `Wait ${s}s`,
  messageTooLongSplit: (n) => `Message will be sent in ${n} parts`,
  send: () => "Send",
  recordingCancelled: () => "Recording cancelled",
  sendAs: () => "Send as",
  sendAsPicture: () => "Picture",
  sendAsPictureHint: () => "1024 px, up to 300 KiB",
  sendAsFile: () => "File",
  sendAsFileHint: (b) => `The original, up to ${Math.floor(b / (1024 * 1024))} MiB`,
  originalOverLimit: (b) =>
    `An original may be at most ${
      Math.floor(b / (1024 * 1024))
    } MiB — this one is larger, so send it as a picture`,
  typing: (names) =>
    names.length === 1
      ? `${names[0]} is typing…`
      : `${names.join(", ")} are typing…`,

  cardAdd: () => "Add friend",
  cardPending: () => "Request sent",
  cardAccept: () => "Accept",
  cardDecline: () => "Decline",
  cardMessage: () => "Message",
  cardUnfriend: () => "Unfriend",
  cardBlock: () => "Block",
  cardUnblock: () => "Unblock",
  cardPolicyClosed: () => "Requests are closed",
  requestsTitle: () => "Friend requests",
  toastRequest: (who) => `${who}: friend request`,
  toastAccepted: (who) => `${who}: request accepted`,

  ringIncoming: (who, channel) => `${who} is calling in “${channel}”`,
  ringJoinHint: () => "Answer",
  ringDecline: () => "Decline",
  callPill: (n) => `Call · ${n}`,

  voiceJoin: () => "Join",
  voiceConnecting: () => "Connecting…",
  voiceKeyExchange: () => "Securing…",
  voiceLiveRtt: (ms) => `Microphone on · ${ms} ms`,
  voiceMutedState: () => "Microphone off",
  voiceDeafenedState: () => "Sound is off",
  voicePoorLoss: (pct) => `Poor connection · ${pct.toFixed(0)}% loss`,
  voicePoorRtt: (ms) => `Poor connection · ${ms} ms`,
  voiceMute: () => "Mute",
  voiceUnmute: () => "Unmute",
  voiceDeafen: () => "Mute sound",
  voiceUndeafen: () => "Unmute sound",
  voiceDisconnect: () => "Leave",
  voiceE2eeNote: () => "The call is end-to-end encrypted. Chat isn't",
  voiceCode: () => "Verification code",
  voiceMicDenied: () => "Allow microphone access",
  voiceUnsupported: () => "Update Chrome to make calls",
  voiceDropped: () => "Call ended unexpectedly",

  watchStream: () => "Watch stream",
  stopWatching: () => "Close stream",
  streamOf: (name) => `${name}'s stream`,
  streamPaused: () => "Stream paused",
  shareVolume: () => "Stream volume",
  streamEndedStopped: () => "Stream ended",
  streamEndedSourceGone: () => "Window closed",
  streamEndedLeft: () => "Streamer left the call",
  streamEndedAdmin: () => "Admin stopped the stream",

  consoleTitle: (name) => `“${name}” settings`,
  consoleDone: () => "Done",
  consoleCancel: () => "Cancel",
  consoleAccess: () => "Access",
  consolePrivate: () => "Private channel",
  consolePassword: () => "Password",
  consolePwKept: () => "unchanged",
  consolePwNone: () => "no password",
  consolePwRemove: () => "Remove",
  consolePwWillRemove: () => "Password will be removed after saving",
  consoleLimits: () => "Limits",
  consoleVoiceLimit: (s) => `Voice message up to ${s}s`,
  consoleMediaLimit: (mb) => `Files up to ${mb} MB`,
  consoleCharsLimit: (n) => `Message up to ${n} characters`,
  consoleSlowmode: (s) => (s === 0 ? "Slow mode: off" : `Slow mode: ${s} s`),
  consoleVoiceEnabled: () => "Calls",
  consoleInvite: () => "Invite",
  consoleInviteNone: () => "No link",
  consoleInviteCreate: () => "Create",
  consoleInviteCopy: () => "Copy",
  consoleInviteCopied: () => "Link copied",
  consoleInviteRefresh: () => "New link",
  consoleInviteRevoke: () => "Delete",
  consoleInviteTtl: () => "The link lasts 3 days. A new link replaces it",
  consoleAppearance: () => "Appearance",
  consoleBgLight: () => "Background in light theme",
  consoleBgDark: () => "Background in dark theme",
  consoleBgPick: () => "Pick",
  consoleBgRemove: () => "Remove",
  consoleBgSet: () => "set",
  consoleBgNone: () => "none",
  consoleBgWillRemove: () => "will be removed",
  consoleBgBlur: (n) => `Background blur: ${n}`,
  consoleBgBrightness: (n) => `Background brightness: ${n}`,
  consolePeople: () => "People",
  consoleRoleOwner: () => "owner",
  consoleRoleAdmin: () => "admin",
  consoleMakeAdmin: () => "Make admin",
  consoleRevokeAdmin: () => "Remove admin",
  consoleMakeOwner: () => "Transfer channel",
  consoleVoiceMute: () => "Mute",
  consoleVoiceUnmute: () => "Unmute",
  consoleKick: () => "Remove",
  consoleBanDay: () => "Ban for a day",
  consoleBanForever: () => "Ban forever",
  consoleBanned: () => "Banned",
  consoleBanUntil: (when) => `until ${when}`,
  consoleBanPermanent: () => "forever",
  consoleUnban: () => "Unban",

  menuReport: () => "Report",
  reportTitle: () => "Report",
  reportReasonLabel: (key) => {
    switch (key) {
      case "abuse": return "Abuse";
      case "sexual": return "Sexual content";
      case "child": return "Child endangerment";
      case "violence": return "Violence";
      case "illegal": return "Illegal";
      case "spam": return "Spam";
      default: return "Other";
    }
  },
  reportCommentPlaceholder: () => "Comment (optional)",
  reportSend: () => "Send",
  reportSent: () => "Report sent",
  reportsTitle: () => "Reports",
  reportsOpen: (n) => `Open: ${n}`,
  reportsShowResolved: () => "Show closed",
  reportsRemove: () => "Removed",
  reportsDismiss: () => "Dismiss",
  reportsEmpty: () => "No reports",


  menuReact: () => "React",
  menuReply: () => "Reply",
  menuEdit: () => "Edit",
  menuDelete: () => "Delete",

  svcCallStarted: (who) => `${who} starts a call`,
  svcCallLive: (n) => `Call · ${n} ${plural("en", n, "participant", "participants", "participants")}`,
  svcCallEnded: (d) => (d ? `Call ended · ${d}` : "Call ended"),
  svcJoined: (who) => `${who} joined the channel`,
  svcLeft: (who) => `${who} leaves the channel`,
  svcRemoved: (who, whom) => `${who} removes ${whom}`,
  svcAdminGranted: (who, whom) => `${who} makes ${whom} an admin`,
  svcAdminRevoked: (who, whom) => `${who} removes ${whom} as admin`,
  svcRenamed: (who, name) => `${who} renames the channel to “${name}”`,
  svcBanned: (who, whom, until) =>
    until ? `${who} bans ${whom} until ${until}` : `${who} bans ${whom}`,
  svcUnbanned: (who, whom) => `${who} lifts ${whom}'s ban`,
  svcVoiceMuted: (who, whom, until) =>
    until ? `${who} mutes ${whom} until ${until}` : `${who} mutes ${whom}`,
  svcVoiceUnmuted: (who, whom) => `${who} unmutes ${whom}`,
  svcOwnerChanged: (who, whom) => `${who} hands the channel to ${whom}`,
  svcUnknown: () => "Update EchoFloat to see this event",
  svcPreview: (kind) => {
    switch (kind) {
      case "call": return "Call";
      case "joined": return "New member";
      case "left": return "Member left";
      case "removed": return "Member removed";
      case "admin_granted": return "New admin";
      case "admin_revoked": return "Admin revoked";
      case "renamed": return "Channel renamed";
      case "banned": return "Ban";
      case "unbanned": return "Ban lifted";
      case "voice_muted": return "Muted";
      case "voice_unmuted": return "Unmuted";
      case "owner_changed": return "New owner";
      default: return "Event";
    }
  },

  durationS: (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s}s`;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  },
};

export function dict(lang: Lang): Dict {
  return lang === "ru" ? ru : en;
}
